import React, { useState } from 'react';
import { Calendar, ChevronDown, ChevronUp, ExternalLink, ListChecks, Users, Video } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import type { ProjectMeetingNote } from '../../types';

export interface GroupMeetingNotesTabProps {
  notes: ProjectMeetingNote[];
}

function summaryPreview(text: string, maxLines = 3): string {
  const lines = (text || '').trim().split(/\n+/).filter(Boolean);
  if (lines.length <= maxLines) return lines.join('\n');
  return `${lines.slice(0, maxLines).join('\n')}…`;
}

const GroupMeetingNotesTab: React.FC<GroupMeetingNotesTabProps> = ({ notes }) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (notes.length === 0) {
    return (
      <div className="tc-empty tc-meeting-empty">
        <Video size={22} className="tc-meeting-empty-icon" aria-hidden />
        <p>No meeting notes for this project yet.</p>
        <p className="tc-meeting-empty-hint">
          After your Fathom agent links a recorded meeting to this project, summaries and action items
          will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="tc-meeting-tab" role="list">
      {notes.map(note => {
        const id = note.id || note.fathomMeetingId;
        const expanded = expandedIds.has(id);
        const openActions = note.actionItems.filter(a => a.status !== 'done').length;
        const doneActions = note.actionItems.filter(a => a.status === 'done').length;

        return (
          <article key={id} className="tc-meeting-card" role="listitem">
            <header className="tc-meeting-card-head">
              <div className="tc-meeting-card-title-wrap">
                <div className="tc-meeting-card-title-row">
                  <h4 className="tc-meeting-card-title">{note.title}</h4>
                  {note.audience ? (
                    <span
                      className={`tc-meeting-audience tc-meeting-audience--${note.audience}`}
                      title={note.audienceRationale || undefined}
                    >
                      {note.audience === 'internal' ? 'Internal' : 'External'}
                    </span>
                  ) : null}
                </div>
                <div className="tc-meeting-card-meta">
                  <span className="tc-meeting-card-date">
                    <Calendar size={12} aria-hidden />
                    {format(note.meetingAt, 'MMM d, yyyy')}
                    <span className="tc-meeting-card-relative">
                      · {formatDistanceToNow(note.meetingAt, { addSuffix: true })}
                    </span>
                  </span>
                  {note.location ? (
                    <span className="tc-meeting-card-location" title={note.location}>
                      {note.location}
                    </span>
                  ) : null}
                  {note.participants && note.participants.length > 0 ? (
                    <span className="tc-meeting-card-participants" title={note.participants.join(', ')}>
                      <Users size={12} aria-hidden />
                      {note.participants.slice(0, 3).join(', ')}
                      {note.participants.length > 3 ? ` +${note.participants.length - 3}` : ''}
                    </span>
                  ) : null}
                </div>
              </div>
              {note.recordingUrl ? (
                <a
                  className="tc-meeting-recording-link"
                  href={note.recordingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                >
                  <ExternalLink size={13} aria-hidden />
                  Recording
                </a>
              ) : null}
            </header>

            {note.summary ? (
              <div className="tc-meeting-summary">
                <p className={expanded ? 'tc-meeting-summary-body' : 'tc-meeting-summary-body tc-meeting-summary-body--clamped'}>
                  {expanded ? note.summary : summaryPreview(note.summary)}
                </p>
                {note.summary.split(/\n+/).filter(Boolean).length > 3 || note.summary.length > 280 ? (
                  <button
                    type="button"
                    className="tc-meeting-summary-toggle"
                    onClick={() => toggleExpanded(id)}
                  >
                    {expanded ? (
                      <>
                        <ChevronUp size={13} aria-hidden /> Show less
                      </>
                    ) : (
                      <>
                        <ChevronDown size={13} aria-hidden /> Show full summary
                      </>
                    )}
                  </button>
                ) : null}
              </div>
            ) : null}

            {note.actionItems.length > 0 ? (
              <section className="tc-meeting-actions">
                <div className="tc-meeting-actions-head">
                  <ListChecks size={14} aria-hidden />
                  <span>
                    Action items
                    {openActions > 0 ? ` · ${openActions} open` : ''}
                    {doneActions > 0 ? ` · ${doneActions} done` : ''}
                  </span>
                </div>
                <ul className="tc-meeting-action-list">
                  {note.actionItems.map(item => (
                    <li
                      key={item.id}
                      className={`tc-meeting-action-item${item.status === 'done' ? ' tc-meeting-action-item--done' : ''}`}
                    >
                      <span className="tc-meeting-action-status" aria-hidden>
                        {item.status === 'done' ? '✓' : '○'}
                      </span>
                      <div className="tc-meeting-action-body">
                        <span className="tc-meeting-action-text">{item.text}</span>
                        {(item.assigneeName || item.dueDate) && (
                          <span className="tc-meeting-action-meta">
                            {item.assigneeName ? item.assigneeName : null}
                            {item.assigneeName && item.dueDate ? ' · ' : null}
                            {item.dueDate ? `Due ${item.dueDate}` : null}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {note.matchRationale ? (
              <p className="tc-meeting-match-rationale" title="Why the agent linked this meeting to the project">
                Linked: {note.matchRationale}
              </p>
            ) : null}
          </article>
        );
      })}
    </div>
  );
};

export default GroupMeetingNotesTab;
